import { useExpanded, useFlexLayout, usePagination, useResizeColumns, useTable } from "react-table";
import classnames from "classnames";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import CustomizedSelect from "../Input/CustomizedSelect";
import { Fragment } from "react";
import { DEFAULT_PAGE_SIZE } from "../../constants";
import "./ReactTableOrange.css";
import { alpha } from "@mui/material";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    "&::-webkit-scrollbar": {
        height: 10,
        width: 10,
    },
    "&::-webkit-scrollbar-track": {
        backgroundColor: "#FCC6B0",
    },
    "&::-webkit-scrollbar-thumb": {
        backgroundColor: alpha(theme.palette.secondary.main, 0.5),
        borderRadius: 50,
    },
}));

const StyledHeaderTableRow = styled(TableRow)(({ theme }) => ({
    backgroundColor: theme.palette.secondary.main,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 15,
        color: theme.palette.neutral.dark,
    },
    borderBottom: "none",
    "&.rt-th": {
        textAlign: "center!important",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: "#FCC6B0",
    },
    "&:nth-of-type(even)": {
        backgroundColor: "#FEE2D7",
    },
    "&:nth-of-type(odd) .MuiTableCell-root": {
        backgroundColor: "#FCC6B0",
    },
    "&:nth-of-type(even) .MuiTableCell-root": {
        backgroundColor: "#FEE2D7",
    },
    "&:nth-of-type(even) .epi-to-check": {
        backgroundColor: "#FFA3A3!important",
    },
    "&:nth-of-type(even) .MuiTableCell-root .epi-to-check": {
        backgroundColor: "#FFA3A3!important",
    },
    minHeight: "6vh",
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    boxShadow: "5px 5px 10px rgba(0, 0, 0, 0.25)",
}));

const ReactTableOrange = (props) => {
    const { className = "", data, getTdProps = () => {}, trClassName = "", SubComponent } = props;

    const columnsFromProps = props.columns;

    /* Defining default column width for every column */
    const defaultColumn = {
        minWidth: 10,
        width: 20,
        maxWidth: 40,
    };

    /* If the table is expandable then add the expander column */
    if (SubComponent) {
        columnsFromProps.unshift({
            Header: "",
            id: "expander",
            resizable: false,
            className: "text-center",
            Cell: ({ row }) => {
                return (
                    //Applying the toggle expander props i.e onClick, style and title
                    <div
                        {...row.getToggleRowExpandedProps()}
                        title="Click here to see more information"
                        className="rt-td rt-expandable p-0"
                    >
                        <div className={classnames("rt-expander", row.isExpanded ? "-open" : "")}>•</div>
                    </div>
                );
            },
        });
    }

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        state: { pageIndex, pageSize },
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        columns,
    } = useTable(
        {
            columns: columnsFromProps,
            data,
            defaultColumn,
            SubComponent,
            initialState: { pageSize: props.pageSize ?? DEFAULT_PAGE_SIZE },
        },
        useFlexLayout,
        useResizeColumns,
        useExpanded,
        usePagination
    );

    return (
        //Applying the table props
        <StyledTableContainer component={StyledPaper} className={classnames(className, "ReactTable")}>
            <Table
                {...getTableProps}
                className="rt-table"
                sx={{ minWidth: 300 }}
                stickyHeader={true}
                aria-label="simple table"
            >
                <TableHead
                    className="rt-thead-header"
                    style={{
                        position: "sticky",
                        top: 0,
                    }}
                >
                    {
                        // Looping over the header rows
                        headerGroups.map((headerGroup, index) => (
                            //Applying the header row props
                            <StyledHeaderTableRow {...headerGroup.getHeaderGroupProps()} className="rt-tr" key={index}>
                                {
                                    //Looping over the headers in each row
                                    headerGroup.headers.map((column, index) => {
                                        /*Checking if column can be resized and applying resizable props along with
                                        header cell props and finally rendering the header cell
                                        */
                                        return column.resizable === false ? (
                                            <StyledTableCell
                                                {...column.getHeaderProps()}
                                                className={classnames(
                                                    "rt-th table-cell",
                                                    column.className,
                                                    props.firstColumnSticky && index === 0 ? "sticky-position" : ""
                                                )}
                                            >
                                                {column.render("Header")}
                                            </StyledTableCell>
                                        ) : (
                                            <StyledTableCell
                                                {...column.getHeaderProps()}
                                                className={classnames(
                                                    "rt-th rt-resizable-header table-cell",
                                                    column.className,
                                                    props.firstColumnSticky && index === 0 ? "sticky-position" : ""
                                                )}
                                            >
                                                <div
                                                    className={classnames(
                                                        "rt-resizable-header-content",
                                                        column.className,
                                                        props.centerResponseColumn && index !== 0
                                                            ? "center-content"
                                                            : ""
                                                    )}
                                                >
                                                    {column.render("Header")}
                                                </div>
                                                <div {...column.getResizerProps()} className="rt-resizer" />
                                            </StyledTableCell>
                                        );
                                    })
                                }
                            </StyledHeaderTableRow>
                        ))
                    }
                </TableHead>
                <TableBody {...getTableBodyProps()} className="rt-tbody rt-tr-group">
                    {
                        /* Applying the table body props*/
                        //Loopting over the table rows
                        page.map((row, ind) => {
                            //Preparing row for rendering
                            prepareRow(row);
                            return (
                                <Fragment key={row.id}>
                                    {/*Applying the row props and adding classname for alternate rows*/}
                                    <StyledTableRow {...row.getRowProps()} className={classnames("rt-tr", trClassName)}>
                                        {
                                            //Looping over the row cells
                                            row.cells.map((cell, cellIndex) => {
                                                return (
                                                    //Applying the cell props and rendering the cell content
                                                    <StyledTableCell
                                                        {...getTdProps(cell)}
                                                        {...cell.getCellProps()}
                                                        onClick={
                                                            props.getTrProps
                                                                ? props.getTrProps(row, cell).onClick
                                                                : undefined
                                                        }
                                                        className={classnames(
                                                            "rt-td",
                                                            cell.column.className,
                                                            "table-cell",
                                                            props.firstColumnSticky && cellIndex === 0
                                                                ? "sticky-position"
                                                                : "",
                                                            props.centerResponseColumn && cellIndex !== 0
                                                                ? "center-content"
                                                                : "",
                                                            props.getTrProps
                                                                ? props.getTrProps(row, cell).className
                                                                : ""
                                                        )}
                                                        key={cellIndex}
                                                    >
                                                        {cell.render("Cell")}
                                                    </StyledTableCell>
                                                );
                                            })
                                        }
                                    </StyledTableRow>
                                    {/* Checking row expansion */}
                                    {row.isExpanded ? SubComponent({ row }) : null}
                                </Fragment>
                            );
                        })
                    }
                </TableBody>
            </Table>
            {props.withPagination ? (
                <div>
                    <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                        {"<<"}
                    </button>{" "}
                    <button onClick={() => previousPage()} disabled={!canPreviousPage}>
                        {"<"}
                    </button>{" "}
                    <button onClick={() => nextPage()} disabled={!canNextPage}>
                        {">"}
                    </button>{" "}
                    <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                        {">>"}
                    </button>{" "}
                    <span>
                        Page{" "}
                        <strong>
                            {pageIndex + 1} of {pageOptions.length}
                        </strong>{" "}
                    </span>
                    <span>
                        | Aller à la page:{" "}
                        <input
                            type="number"
                            defaultValue={pageIndex + 1}
                            onChange={(e) => {
                                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                gotoPage(page);
                            }}
                            style={{ width: "100px" }}
                        />
                    </span>{" "}
                    <CustomizedSelect
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                        }}
                    >
                        {[10, 20, 30, 40, 50].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                Afficher {pageSize}
                            </option>
                        ))}
                    </CustomizedSelect>
                </div>
            ) : null}
        </StyledTableContainer>
    );
};

export default ReactTableOrange;
